import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { ArrowDown, Flame, Sparkles } from "lucide-react";
import { useLanguage } from "./LanguageContext";

export default function Hero() {
  const { t } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });

  // Scroll parallax handlers
  const { scrollY } = useScroll();
  const textY = useTransform(scrollY, [0, 600], [0, 80]);
  const textOpacity = useTransform(scrollY, [0, 500], [1, 0]);
  const bgScale = useTransform(scrollY, [0, 800], [1, 1.08]);

  // Track mouse coordinates for interactive glow and subtle parallax
  useEffect(() => {
    let rect = containerRef.current ? containerRef.current.getBoundingClientRect() : null;

    const handleScrollOrResize = () => {
      if (containerRef.current) {
        rect = containerRef.current.getBoundingClientRect();
      }
    };

    window.addEventListener("scroll", handleScrollOrResize, { passive: true });
    window.addEventListener("resize", handleScrollOrResize, { passive: true });

    const handleMouseMove = (e: MouseEvent) => {
      if (containerRef.current) {
        if (!rect) {
          rect = containerRef.current.getBoundingClientRect();
        }
        const x = e.clientX - (rect ? rect.left : 0);
        const y = e.clientY - (rect ? rect.top : 0);
        mouseRef.current = { x, y };

        // Set CSS variables for pointer glow tracking
        containerRef.current.style.setProperty("--x", `${x}px`);
        containerRef.current.style.setProperty("--y", `${y}px`);
      }
    };
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize);
      window.removeEventListener("resize", handleScrollOrResize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  // HTML5 Interactive Canvas particles & light beams
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    // Particle pool definition
    const particleCount = 45;
    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      alpha: number;
      color: string;
    }> = [];

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 2.5 + 0.5,
        alpha: Math.random() * 0.5 + 0.1,
        color: i % 2 === 0 ? "79, 70, 229" : "129, 140, 248", // Indigo & Light Indigo
      });
    }

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize, { passive: true });

    // Rendering loop
    const render = () => {
      ctx.clearRect(0, 0, width, height);

      // Draw subtle orbital mesh lines
      ctx.strokeStyle = "rgba(79, 70, 229, 0.03)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i < width; i += 120) {
        ctx.moveTo(i, 0);
        ctx.bezierCurveTo(i + 40, height / 3, i - 40, (height * 2) / 3, i, height);
      }
      ctx.stroke();

      // Render floating particle points
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        // Boundary wrap
        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;

        // Mouse attraction
        const dx = mouseRef.current.x - p.x;
        const dy = mouseRef.current.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 250) {
          p.x += (dx / dist) * 0.15;
          p.y += (dy / dist) * 0.15;
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color}, ${p.alpha})`;
        ctx.fill();
      });

      // Render custom subtle light beam grids crossing the viewport
      ctx.fillStyle = "rgba(79, 70, 229, 0.005)";
      ctx.beginPath();
      ctx.moveTo(width * 0.2, 0);
      ctx.lineTo(width * 0.5, height);
      ctx.lineTo(width * 0.45, height);
      ctx.lineTo(width * 0.15, 0);
      ctx.fill();

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <section
      ref={containerRef}
      className="relative w-full h-screen overflow-hidden bg-white flex items-center justify-center cursor-glow"
      style={{
        "--x": `${window.innerWidth / 2}px`,
        "--y": `${window.innerHeight / 2}px`,
      } as React.CSSProperties}
    >
      {/* Background Interactive canvas */}
      <motion.div style={{ scale: bgScale }} className="absolute inset-0 z-0">
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full opacity-70" />
        <div className="absolute inset-0 bg-radial-[circle_at_center,transparent_40%,rgba(255,255,255,1)_95%]" />
        
        {/* Soft floating blurred lights */}
        <div className="absolute top-1/4 left-1/3 w-80 h-80 ambient-light-purple rounded-full blur-3xl animate-float opacity-45 pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 ambient-light-indigo rounded-full blur-3xl animate-float opacity-50 pointer-events-none" style={{ animationDelay: "2s" }} />
        
        {/* Cinematic Grid Lines Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,0,0,0.015)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.012)_1px,transparent_1px)] bg-[size:5rem_5rem] pointer-events-none opacity-40" />
      </motion.div>

      {/* Foreground Hero Layout */}
      <motion.div
        style={{ y: textY, opacity: textOpacity }}
        className="relative z-10 max-w-4xl mx-auto px-6 text-center select-none"
      >
        {/* Custom luxury tag indicator */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full glass-card text-[10px] font-mono tracking-widest text-indigo-600 uppercase mb-8"
        >
          <Sparkles size={11} className="animate-pulse" /> {t("tag", "hero")}
        </motion.div>

        {/* Cinematic Headings */}
        <h1 className="font-display text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight text-[#111827] leading-[1.05] mb-8">
          <span className="block overflow-hidden pb-1">
            <motion.span
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="inline-block"
            >
              {t("line1", "hero")}
            </motion.span>
          </span>
          <span className="block overflow-hidden py-1">
            <motion.span
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1.4, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="inline-block bg-gradient-to-r from-brand-charcoal via-indigo-600 to-indigo-500 bg-clip-text text-transparent"
            >
              {t("line2", "hero")}
            </motion.span>
          </span>
        </h1>

        {/* Elegant cinematic subheading */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="text-base sm:text-lg md:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed font-sans mb-12 font-light"
        >
          {t("sub", "hero")}
        </motion.p>

        {/* CTA Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.4, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col sm:flex-row items-center justify-center gap-5"
        >
          {/* Main action CTA */}
          <a
            href="#products"
            className="group w-full sm:w-auto relative inline-flex items-center justify-center bg-brand-charcoal hover:bg-indigo-600 text-white font-mono text-xs tracking-widest px-8 py-4.5 rounded-full transition-all duration-300 focus:outline-hidden overflow-hidden shadow-xl"
          >
            <div className="absolute inset-0 w-full h-full bg-linear-to-r from-indigo-500 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <span className="relative z-10 flex items-center gap-2">
              {t("ctaPrimary", "hero")} <ArrowDown size={14} className="group-hover:translate-y-1 transition-transform" />
            </span>
          </a>

          {/* Secondary contact action */}
          <a
            href="#contact"
            className="w-full sm:w-auto relative inline-flex items-center justify-center text-gray-700 hover:text-black font-mono text-xs tracking-widest px-8 py-4.5 rounded-full border border-gray-250 hover:border-gray-999 transition-all backdrop-blur-xs focus:outline-hidden"
          >
            {t("ctaSecondary", "hero")}
          </a>
        </motion.div>
      </motion.div>

      {/* Floating interactive scroll prompt */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 pointer-events-none flex flex-col items-center gap-1.5"
      >
        <span className="text-[9px] font-mono tracking-widest uppercase text-gray-400">{t("scrollDown", "hero")}</span>
        <div className="w-1 px-0.5 h-7 bg-indigo-50/20 rounded-full relative overflow-hidden flex items-start justify-center">
          <motion.div
            animate={{ y: [0, 16, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-1.5 h-1.5 bg-indigo-600 rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
}
